import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, field, StringDecoder } from "@simonbackx/simple-encoding";
import { isSimpleError, isSimpleErrors, SimpleErrors } from "@simonbackx/simple-errors";
import { StringCompare } from "@stamhoofd/utility";
import { v4 as uuidv4 } from "uuid";
import { ChoicesFilterChoice, ChoicesFilterDefinition, ChoicesFilterMode } from "../../filters/ChoicesFilter";
import { DateFilterDefinition } from "../../filters/DateFilter";
import { NumberFilterDefinition } from "../../filters/NumberFilter";
import { PropertyFilter } from "../../filters/PropertyFilter";
import { StringFilterDefinition } from "../../filters/StringFilter";
import { RecordCheckboxAnswer, RecordChooseOneAnswer, RecordDateAnswer, RecordMultipleChoiceAnswer, RecordPriceAnswer, RecordTextAnswer } from "./RecordAnswer";
import { RecordSettings, RecordType } from "./RecordSettings";
export class RecordEditorSettings {
    constructor(options) {
        this.dataPermission = false;
        Object.assign(this, options);
    }
}
export class RecordCategory extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.name = "";
        this.description = "";
        /**
         * A category can either have childCategories or records, but never both. Records are ignored as soon as the category has at least one child category.
         * Currently we only support 2 categories deep
         */
        this.childCategories = [];
        this.records = [];
        this.filter = null;
    }
    getAllRecords() {
        if (this.childCategories.length > 0) {
            return [...this.records, ...this.childCategories.flatMap(c => c.getAllRecords())];
        }
        return this.records;
    }
    getAllFilteredRecords(filterValue, filterDefinitions, dataPermission) {
        if (this.childCategories.length > 0) {
            return [
                ...this.filterChildCategories(filterValue, filterDefinitions, dataPermission).flatMap(c => c.getAllFilteredRecords(filterValue, filterDefinitions, dataPermission)),
                ...this.filterRecords(dataPermission)
            ];
        }
        return this.filterRecords(dataPermission);
    }
    filterRecords(dataPermission) {
        if (dataPermission) {
            return this.records;
        }
        return this.records.filter(r => !r.sensitive);
    }
    isEnabled(filterValue, filterDefinitions, dataPermission) {
        if (this.filter && !this.filter.enabledWhen.decode(filterDefinitions).doesMatch(filterValue)) {
            return false;
        }
        if (this.childCategories.length > 0) {
            if (this.filterChildCategories(filterValue, filterDefinitions, dataPermission).length > 0) {
                return true;
            }
        }
        if (this.filterRecords(dataPermission).length > 0) {
            return true;
        }
        return false;
    }
    isRequired(filterValue, filterDefinitions) {
        var _a;
        if (!this.filter) {
            return false;
        }
        if (this.filter.requiredWhen === null) {
            // Always skippable
            return false;
        }
        const decoded = (_a = this.filter.requiredWhen) === null || _a === void 0 ? void 0 : _a.decode(filterDefinitions);
        if (!decoded) {
            // Failed to decode
            return true;
        }
        if (decoded.doesMatch(filterValue)) {
            return true;
        }
        return false;
    }
    static filterCategories(categories, filterValue, filterDefinitions, dataPermission) {
        return categories.filter(category => {
            return category.isEnabled(filterValue, filterDefinitions, dataPermission);
        });
    }
    filterChildCategories(filterValue, filterDefinitions, dataPermission) {
        return RecordCategory.filterCategories(this.childCategories, filterValue, filterDefinitions, dataPermission);
    }
    /**
     * Flatten all categories and child categories into a single array
     */
    static flattenCategories(categories, filterValue, filterDefinitions, dataPermission) {
        return RecordCategory.filterCategories(categories, filterValue, filterDefinitions, dataPermission).flatMap(cat => {
            // Make a (not deep!) clone
            const cat2 = RecordCategory.create(cat);
            cat2.childCategories = [];
            cat2.records = cat2.filterRecords(dataPermission);
            if (cat.childCategories.length > 0) {
                // Make a (not deep!) clone
                return [
                    ...(cat2.records.length > 0 ? [cat2] : []),
                    ...this.flattenCategories(cat.childCategories, filterValue, filterDefinitions, dataPermission).map(c => {
                        // Make a (not deep!) clone
                        const cc = RecordCategory.create(c);
                        cc.name = cat.name + " → " + c.name;
                        return cc;
                    })
                ];
            }
            return (cat2.records.length > 0 ? [cat2] : []);
        });
    }
    /**
     * Get a flat array with record categories whose records match the filter
     */
    static flattenCategoriesWith(categories, filter) {
        return categories.flatMap(cat => {
            // Make a (not deep!) clone
            const cat2 = RecordCategory.create(cat);
            const updatedRecords = cat.records.filter(r => {
                return filter(r);
            });
            cat2.records = updatedRecords;
            if (cat.childCategories.length > 0) {
                // Make a (not deep!) clone
                cat2.childCategories = [];
                return [
                    ...(cat2.records.length > 0 ? [cat2] : []),
                    ...this.flattenCategoriesWith(cat.childCategories, filter).map(c => {
                        // Make a (not deep!) clone
                        const cc = RecordCategory.create(c);
                        cc.name = cat.name + " → " + c.name;
                        return cc;
                    })
                ];
            }
            return cat2.records.length > 0 ? [cat2] : [];
        });
    }
    /**
     * Remove (child) categories that don't have a record that matches the filter
     */
    static filterRecordsWith(categories, filter) {
        return categories.flatMap(cat => {
            // Make a (not deep!) clone
            const cat2 = RecordCategory.create(cat);
            const updatedRecords = cat.records.filter(r => {
                return filter(r);
            });
            cat2.records = updatedRecords;
            cat2.childCategories = this.filterRecordsWith(cat.childCategories, filter);
            return cat2.records.length > 0 || cat2.childCategories.length > 0 ? [cat2] : [];
        });
    }
    /**
     * Get all categories for the given answers
     */
    static flattenCategoriesForAnswers(categories, answers) {
        return this.flattenCategoriesWith(categories, r => {
            return !!answers.find(a => a.settings.id == r.id);
        });
    }
    static getRecordCategoryDefinitions(recordCategories, getAnswers) {
        const definitions = [];
        for (const recordCategory of recordCategories) {
            for (const record of recordCategory.records) {
                definitions.push(...this.filterDefinitionsFromRecord(record, recordCategory.name, getAnswers));
            }
            for (const category of recordCategory.childCategories) {
                for (const record of category.getAllRecords()) {
                    definitions.push(...this.filterDefinitionsFromRecord(record, recordCategory.name + ' → ' + category.name, getAnswers));
                }
            }
        }
        return definitions;
    }
    static filterDefinitionsFromRecord(record, category, getAnswers) {
        if (record.type === RecordType.Checkbox) {
            return [
                new ChoicesFilterDefinition({
                    id: "record_" + record.id,
                    name: record.name,
                    category,
                    choices: [
                        new ChoicesFilterChoice("checked", "Aangevinkt"),
                        new ChoicesFilterChoice("not_checked", "Niet aangevinkt")
                    ],
                    getValue: (v) => {
                        const answers = getAnswers(v);
                        const answer = answers.find(a => { var _a; return ((_a = a.settings) === null || _a === void 0 ? void 0 : _a.id) === record.id; });
                        if (answer instanceof RecordCheckboxAnswer) {
                            return (answer === null || answer === void 0 ? void 0 : answer.selected) ? ["checked"] : ["not_checked"];
                        }
                        return ["not_checked"];
                    },
                    defaultMode: ChoicesFilterMode.Or
                })
            ];
        }
        if (record.type === RecordType.MultipleChoice) {
            return [
                new ChoicesFilterDefinition({
                    id: "record_" + record.id,
                    name: record.name,
                    category,
                    choices: record.choices.map(c => new ChoicesFilterChoice(c.id, c.name)),
                    getValue: (v) => {
                        const answers = getAnswers(v);
                        const answer = answers.find(a => { var _a; return ((_a = a.settings) === null || _a === void 0 ? void 0 : _a.id) === record.id; });
                        let choices = [];
                        if (!answer || !(answer instanceof RecordMultipleChoiceAnswer)) {
                            if (answer && (answer instanceof RecordChooseOneAnswer) && answer.selectedChoice) {
                                choices = [answer.selectedChoice];
                            }
                        }
                        else {
                            choices = answer.selectedChoices;
                        }
                        // Map choices to valid choices on id or name
                        return choices.map(c => {
                            var _a, _b;
                            const choice = (_b = (_a = record.choices.find(ch => ch.id === c.id)) !== null && _a !== void 0 ? _a : record.choices.find(ch => StringCompare.typoCount(ch.name, c.name) === 0)) !== null && _b !== void 0 ? _b : record.choices.find(ch => StringCompare.typoCount(ch.name, c.name) < 2);
                            if (choice) {
                                return choice.id;
                            }
                            return c.id;
                        });
                    },
                    defaultMode: ChoicesFilterMode.And
                })
            ];
        }
        if (record.type === RecordType.ChooseOne) {
            return [
                new ChoicesFilterDefinition({
                    id: "record_" + record.id,
                    name: record.name,
                    category,
                    choices: record.choices.map(c => new ChoicesFilterChoice(c.id, c.name)),
                    getValue: (v) => {
                        const answers = getAnswers(v);
                        const answer = answers.find(a => { var _a; return ((_a = a.settings) === null || _a === void 0 ? void 0 : _a.id) === record.id; });
                        let choices = [];
                        if (!answer || !(answer instanceof RecordChooseOneAnswer) || !answer.selectedChoice) {
                            if (answer && (answer instanceof RecordMultipleChoiceAnswer)) {
                                choices = answer.selectedChoices;
                            }
                        }
                        else {
                            choices = [answer.selectedChoice];
                        }
                        // Map choices to valid choices on id or name
                        return choices.map(c => {
                            var _a, _b;
                            const choice = (_b = (_a = record.choices.find(ch => ch.id === c.id)) !== null && _a !== void 0 ? _a : record.choices.find(ch => StringCompare.typoCount(ch.name, c.name) === 0)) !== null && _b !== void 0 ? _b : record.choices.find(ch => StringCompare.typoCount(ch.name, c.name) < 2);
                            if (choice) {
                                return choice.id;
                            }
                            return c.id;
                        });
                    },
                    defaultMode: ChoicesFilterMode.Or
                })
            ];
        }
        if (record.type === RecordType.Text || record.type === RecordType.Textarea) {
            return [
                new StringFilterDefinition({
                    id: "record_" + record.id,
                    name: record.name,
                    category,
                    getValue: (v) => {
                        var _a, _b;
                        const answers = getAnswers(v);
                        const answer = answers.find(a => { var _a; return ((_a = a.settings) === null || _a === void 0 ? void 0 : _a.id) === record.id; });
                        if (answer instanceof RecordTextAnswer) {
                            return (_a = answer === null || answer === void 0 ? void 0 : answer.value) !== null && _a !== void 0 ? _a : "";
                        }
                        return (_b = answer === null || answer === void 0 ? void 0 : answer.stringValue) !== null && _b !== void 0 ? _b : "";
                    }
                })
            ];
        }
        if (record.type === RecordType.Price) {
            return [
                new NumberFilterDefinition({
                    id: "record_" + record.id,
                    name: record.name,
                    currency: true,
                    floatingPoint: true,
                    category,
                    getValue: (v) => {
                        var _a;
                        const answers = getAnswers(v);
                        const answer = answers.find(a => { var _a; return ((_a = a.settings) === null || _a === void 0 ? void 0 : _a.id) === record.id; });
                        if (answer instanceof RecordPriceAnswer) {
                            return (_a = answer.value) !== null && _a !== void 0 ? _a : 0;
                        }
                        if (typeof (answer === null || answer === void 0 ? void 0 : answer.objectValue) === "number" && !isNaN(answer.objectValue)) {
                            return answer.objectValue;
                        }
                        return 0;
                    }
                })
            ];
        }
        if (record.type === RecordType.Date) {
            return [
                new DateFilterDefinition({
                    id: "record_" + record.id,
                    name: record.name,
                    time: false,
                    category,
                    getValue: (v) => {
                        var _a;
                        const answers = getAnswers(v);
                        const answer = answers.find(a => { var _a; return ((_a = a.settings) === null || _a === void 0 ? void 0 : _a.id) === record.id; });
                        if (answer instanceof RecordDateAnswer) {
                            return (_a = answer.dateValue) !== null && _a !== void 0 ? _a : new Date(1900);
                        }
                        return new Date(1900);
                    }
                })
            ];
        }
        return [];
    }
    static validate(categories, answers, filterValue, filterDefinitions, dataPermission) {
        const filteredCategories = RecordCategory.filterCategories(categories, filterValue, filterDefinitions, dataPermission);
        const errors = new SimpleErrors();
        const cleanedAnswers = [];
        // Delete all records that are not in the list
        for (const category of filteredCategories) {
            const requiredCategory = category.isRequired(filterValue, filterDefinitions);
            for (const record of category.getAllFilteredRecords(filterValue, filterDefinitions, dataPermission)) {
                try {
                    const answer = record.validate(answers, requiredCategory);
                    if (answer && !cleanedAnswers.includes(answer)) {
                        cleanedAnswers.push(answer);
                    }
                }
                catch (e) {
                    if (isSimpleErrors(e) || isSimpleError(e)) {
                        errors.addError(e);
                    }
                    else {
                        throw e;
                    }
                }
            }
        }
        errors.throwIfNotEmpty();
        return cleanedAnswers;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], RecordCategory.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], RecordCategory.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder })
], RecordCategory.prototype, "description", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordCategory) })
], RecordCategory.prototype, "childCategories", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordSettings) })
], RecordCategory.prototype, "records", void 0);
__decorate([
    field({ decoder: PropertyFilter, version: 126, nullable: true })
], RecordCategory.prototype, "filter", void 0);
//# sourceMappingURL=RecordCategory.js.map